/**
 * EquipmentUnitEditorPage — `equipment/01-unit-editor` (egyedi gép-rekord szerkesztő).
 *
 * **Data-slice (seam #353/#354)** a #351 W0-vázra: a 4-tab editor adat-kötése a
 * `equipmentUnitReadResponseSchema` (`@kgc/equipment-type-domain`) read-DTO-ra. A foundationök:
 * `DetailPageTabs` (#348) + `useTabUrlSync` + `ChipInput` (STORY-1104, alt-SKU). A státusz a kanonikus
 * 8-state `EquipmentUnitStatus`-ra köt (8-state `UnitStatusBadge` + custom timeline — a `@kgc/ui`
 * StatusBadge csak 4-statet ismer). A státusz-átmenet (POST /status) külön domain-action slice.
 *
 * Tab-1 szerkeszthető (PATCH): manufacturerSku/altSkus/serialNumber/purchasedAt/hours/note.
 * Readonly: internalSku (auto-gen) · category (a típusról) · typeName (breadcrumb).
 */
import { updateEquipmentUnit } from '@/api/equipment-unit';
import { useEquipmentUnit } from '@/hooks/use-equipment-unit';
import { useTabUrlSync } from '@/hooks/use-tab-url-sync';
import type { EquipmentUnitRead } from '@kgc/equipment-type-domain';
import { useTranslation } from '@kgc/i18n';
import { ChipInput, DetailPageTabs, toast } from '@kgc/ui';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { EquipmentStatusTransitionModal } from './EquipmentStatusTransitionModal';
import { UnitStatusBadge } from './unit-status-badge';

function formatDate(value: Date | null): string {
  return value ? new Date(value).toLocaleDateString('hu-HU') : '—';
}

/** Date → `yyyy-mm-dd` (date-input value). */
function toDateInput(value: Date | null): string {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  if (bytes >= 1000) return `${Math.round(bytes / 1000)} kB`;
  return `${bytes} B`;
}

export function EquipmentUnitEditorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { unit, isLoading, error, refetch } = useEquipmentUnit(id);
  const [tab, setTab] = useTabUrlSync('tab', 'basic');

  if (isLoading) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8">
        <p className="text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }

  if (error || !unit) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8">
        <p className="text-destructive">{error ?? t('common.notFound')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <header className="border-b bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/admin/equipment-types')}
            className="mb-2 text-sm text-muted-foreground hover:text-foreground"
          >
            &larr; {t('equipmentUnit.backToTypes')} / {unit.typeName}
          </button>
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm font-medium text-primary">{unit.internalSku}</span>
            <UnitStatusBadge status={unit.status} size="sm" />
          </div>
          <h1 className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">
            {unit.typeName}
          </h1>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        <DetailPageTabs
          idPrefix="equipment-unit"
          ariaLabel={t('equipmentUnit.tabsAria')}
          value={tab}
          onValueChange={setTab}
          tabs={[
            {
              key: 'basic',
              label: t('equipmentUnit.tabs.basic'),
              content: <BasicTab unit={unit} onSaved={refetch} />,
            },
            {
              key: 'status',
              label: t('equipmentUnit.tabs.status'),
              content: <StatusTab unit={unit} onTransitioned={refetch} />,
            },
            {
              key: 'images',
              label: t('equipmentUnit.tabs.images'),
              content: <ImagesTab unit={unit} />,
            },
            {
              key: 'documents',
              label: t('equipmentUnit.tabs.documents'),
              content: <DocumentsTab unit={unit} />,
            },
          ]}
        />
      </main>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab-1: Alapadatok — szerkeszthető form (PATCH)
// ─────────────────────────────────────────────────────────────────────────────
function BasicTab({ unit, onSaved }: { unit: EquipmentUnitRead; onSaved: () => Promise<void> }) {
  const { t } = useTranslation();
  const [manufacturerSku, setManufacturerSku] = useState('');
  const [altSkus, setAltSkus] = useState<string[]>([]);
  const [serialNumber, setSerialNumber] = useState('');
  const [purchasedAt, setPurchasedAt] = useState('');
  const [hours, setHours] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  // seed a unit-ból (és re-seed ha a unit változik egy save után)
  useEffect(() => {
    setManufacturerSku(unit.manufacturerSku ?? '');
    setAltSkus(unit.altSkus);
    setSerialNumber(unit.serialNumber);
    setPurchasedAt(toDateInput(unit.purchasedAt));
    setHours(unit.hours === null ? '' : String(unit.hours));
    setNote(unit.note ?? '');
  }, [unit]);

  async function handleSave() {
    setSaving(true);
    try {
      await updateEquipmentUnit(unit.id, {
        manufacturerSku: manufacturerSku.trim() === '' ? null : manufacturerSku.trim(),
        altSkus,
        serialNumber: serialNumber.trim(),
        purchasedAt: purchasedAt === '' ? null : new Date(purchasedAt),
        hours: hours.trim() === '' ? null : Number(hours),
        note: note.trim() === '' ? null : note,
      });
      toast.success(t('equipmentUnit.saved'));
      await onSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('equipmentUnit.saveError'));
    } finally {
      setSaving(false);
    }
  }

  const labelCls = 'block text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1';
  const inputCls =
    'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800';

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="eu-manufacturerSku">
            {t('equipmentUnit.field.manufacturerSku')}
          </label>
          <input
            id="eu-manufacturerSku"
            data-testid="eu-manufacturerSku"
            value={manufacturerSku}
            onChange={e => setManufacturerSku(e.target.value)}
            className={`${inputCls} font-mono`}
          />
        </div>
        <div>
          <label className={labelCls}>{t('equipmentUnit.field.internalSku')}</label>
          <input
            readOnly
            value={unit.internalSku}
            data-testid="eu-internalSku"
            className={`${inputCls} cursor-not-allowed bg-gray-50 font-mono text-muted-foreground dark:bg-gray-900`}
          />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} id="eu-altSkus-label">
            {t('equipmentUnit.field.altSkus')}
          </label>
          <ChipInput
            values={altSkus}
            onChange={setAltSkus}
            aria-describedby="eu-altSkus-label"
            placeholder={t('equipmentUnit.altSkusPlaceholder')}
            labels={{
              create: (v: string) => t('equipmentUnit.altSkusCreate', { value: v }),
              duplicate: t('equipmentUnit.altSkusDuplicate'),
              remove: t('common.delete'),
            }}
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="eu-serialNumber">
            {t('equipmentUnit.field.serialNumber')}
          </label>
          <input
            id="eu-serialNumber"
            data-testid="eu-serialNumber"
            value={serialNumber}
            onChange={e => setSerialNumber(e.target.value)}
            className={`${inputCls} font-mono`}
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="eu-purchasedAt">
            {t('equipmentUnit.field.purchasedAt')}
          </label>
          <input
            id="eu-purchasedAt"
            type="date"
            data-testid="eu-purchasedAt"
            value={purchasedAt}
            onChange={e => setPurchasedAt(e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="eu-hours">
            {t('equipmentUnit.field.hours')}
          </label>
          <input
            id="eu-hours"
            type="number"
            step="0.1"
            min="0"
            data-testid="eu-hours"
            value={hours}
            onChange={e => setHours(e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>{t('equipmentUnit.field.category')}</label>
          <input
            readOnly
            data-testid="eu-category"
            value={t(`equipmentUnit.category.${unit.category}`)}
            className={`${inputCls} cursor-not-allowed bg-gray-50 text-muted-foreground dark:bg-gray-900`}
          />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="eu-note">
            {t('equipmentUnit.field.note')}
          </label>
          <textarea
            id="eu-note"
            data-testid="eu-note"
            rows={2}
            value={note}
            onChange={e => setNote(e.target.value)}
            className={inputCls}
          />
        </div>
      </div>

      <div className="flex justify-end border-t border-gray-100 pt-4 dark:border-gray-800">
        <button
          type="button"
          data-testid="eu-save"
          disabled={saving}
          onClick={handleSave}
          className="rounded-md bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground shadow-sm disabled:opacity-60"
        >
          {saving ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab-2: Státusz & előzmény — badge + statusSince + custom timeline
// ─────────────────────────────────────────────────────────────────────────────
function StatusTab({
  unit,
  onTransitioned,
}: {
  unit: EquipmentUnitRead;
  onTransitioned: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {t('equipmentUnit.field.status')}
          </h3>
          <div className="mt-2 flex items-center gap-3">
            <UnitStatusBadge status={unit.status} />
            <span className="text-xs text-muted-foreground">
              {t('equipmentUnit.field.statusSince')}: {formatDate(unit.statusSince)}
            </span>
          </div>
        </div>
        <button
          type="button"
          data-testid="eu-transition-open"
          onClick={() => setModalOpen(true)}
          className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm"
        >
          {t('equipmentUnit.transition.button')}
        </button>
      </div>

      <EquipmentStatusTransitionModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        unitId={unit.id}
        currentStatus={unit.status}
        onTransitioned={onTransitioned}
      />

      <div>
        <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">
          {t('equipmentUnit.statusHistory')}
        </h3>
        <ol
          data-testid="eu-status-history"
          className="relative ml-2 space-y-5 border-l-2 border-gray-200 pl-4 dark:border-gray-700"
        >
          {unit.statusHistory.map((e, i) => (
            <li key={`${e.status}-${i}`} className="relative">
              <span className="absolute -left-[25px] top-1 h-3 w-3 rounded-full bg-gray-300 ring-4 ring-gray-50 dark:bg-gray-600 dark:ring-gray-900" />
              <div className="flex flex-wrap items-center gap-2">
                <UnitStatusBadge status={e.status} size="sm" />
                <span className="font-mono text-[11px] text-muted-foreground">
                  {formatDate(e.at)}
                </span>
                <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground dark:bg-gray-800">
                  {e.durationLabel}
                </span>
              </div>
              <div className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                {e.label}
              </div>
              <div className="text-xs text-muted-foreground">{e.trigger}</div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab-3: Képek
// ─────────────────────────────────────────────────────────────────────────────
function ImagesTab({ unit }: { unit: EquipmentUnitRead }) {
  const { t } = useTranslation();
  if (unit.images.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('equipmentUnit.imagesEmpty')}</p>;
  }
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="eu-images">
      {unit.images.map(img => (
        <figure
          key={img.id}
          className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
        >
          <img
            src={img.url}
            alt={img.caption ?? ''}
            className="aspect-square w-full object-cover"
          />
          {img.caption && (
            <figcaption className="px-2 py-1 text-[11px] text-muted-foreground">
              {img.caption}
            </figcaption>
          )}
        </figure>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab-4: Dokumentumok
// ─────────────────────────────────────────────────────────────────────────────
function DocumentsTab({ unit }: { unit: EquipmentUnitRead }) {
  const { t } = useTranslation();
  if (unit.documents.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('equipmentUnit.documentsEmpty')}</p>;
  }
  return (
    <ul
      data-testid="eu-documents"
      className="divide-y divide-gray-100 rounded-lg border border-gray-200 dark:divide-gray-800 dark:border-gray-700"
    >
      {unit.documents.map(doc => (
        <li key={doc.id} className="flex items-center gap-3 p-3">
          <div className="flex-1">
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{doc.name}</div>
            <div className="text-[11px] text-muted-foreground">
              {doc.kind} · {formatFileSize(doc.sizeBytes)}
              {doc.expiresAt && ` · ${t('equipmentUnit.expiresAt')}: ${formatDate(doc.expiresAt)}`}
            </div>
          </div>
          <a
            href={doc.url}
            className="text-xs font-semibold text-primary hover:underline"
            target="_blank"
            rel="noreferrer"
          >
            {t('equipmentUnit.download')}
          </a>
        </li>
      ))}
    </ul>
  );
}
